<template>
    <v-container fluid class="zapping-view">
        <plan-required-content v-if="locked" inline feature="zapping" />
        <!-- SELECT -->
        <div v-else-if="!showRoutines" class="zap-select">
            <div class="zap-select-card">
                <p class="zap-select-desc">{{ $t('common.zapping_tip') }}</p>
                <kimsa-combobox :label="$t('statistics.select_coach')"
                    :placeholder="$t('statistics.select_coach_placeholder')" dense solo :items="coaches"
                    @change="onSetCurrentCoach" :value="currentCoach" placeholder-class icon="$account" searchable
                    item-value="objectID" :loading="loading" />
                <skeleton-loader v-if="loading" type="zapping-options" />
                <template v-else-if="currentCoach">
                    <select-option-students icon-only-mobile :custom-options="options" :value="option"
                        @change="option = $event" />
                    <template v-if="isTeams">
                        <kimsa-combobox :placeholder="$t('common.select_placeholder')" dense solo :items="teams"
                            @change="team = $event" :value="team" item-value="id" clearable />
                        <kimsa-combobox :placeholder="$t('common.select_placeholder')" dense solo
                            :items="subgroupOptions" @change="subgroup = $event" :value="subgroup" item-value="id"
                            clearable :readonly="!team" />
                    </template>
                    <kimsa-combobox v-if="isGroups" :placeholder="$t('common.select_placeholder')" dense solo
                        :items="groups" @change="group = $event" :value="group" item-value="id" clearable />
                    <zapping-student-picker :items="optionStudents" :value="students" :max-selected="20"
                        :readonly="readonlyStudents" @change="students = $event" @start="showRoutines = true" />
                </template>
            </div>
        </div>
        <!-- RUTINA -->
        <zapping-columns v-else :routines="selectedRoutines" @reset="onReset" />
    </v-container>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import SkeletonLoader from '../../components/common/SkeletonLoader.vue';
import KimsaCombobox from '../../components/common/inputs/KimsaCombobox.vue';
import ZappingColumns from '../../components/coach/routines/ZappingColumns.vue';
import ZappingStudentPicker from '../../components/coach/routines/ZappingStudentPicker.vue';
import { getMergedBlocks, uniqueDayRoutine } from "@/utils/Exercises";
import { getAllByIds } from "@/utils/Firebase";
import SelectOptionStudents from '../../components/coach/SelectOptionStudents.vue';
import PlanRequiredContent from '../../components/common/PlanRequiredContent.vue';

export default {
    components: { SkeletonLoader, KimsaCombobox, ZappingColumns, ZappingStudentPicker, SelectOptionStudents, PlanRequiredContent },
    data(vm) {
        return {
            loading: false,
            optionStudents: [],
            students: [],
            showRoutines: false,
            routines: [],
            mergedBlocksCache: {},
            coaches: [],
            currentCoach: null,
            team: null,
            subgroup: null,
            group: null,
            options: [
                { name: `${this.$t('common.student')}s`, value: 'student', icon: 'mdi-account' },
                { name: `${vm.$t('common.group')}s`, value: 'group', icon: 'mdi-account-multiple' },
                { name: `${vm.$t('common.team')}s`, value: 'team', icon: 'mdi-account-group' },
            ],
            option: 'student',
        }
    },
    computed: {
        ...mapState(['coachAssignedRoutines', 'teams', 'groups', 'libraryCoach']),
        locked(vm = this) {
            return !vm.can('zapping')
        },
        coachId(vm = this) {
            return vm.currentCoach?.objectID || null
        },
        selectedRoutines(vm = this) {
            return [...vm.routines].filter(routine => {
                return vm.students.find(student => student.objectID === routine.studentId)
            })
        },
        isTeams(vm = this) {
            return vm.option === 'team'
        },
        isGroups(vm = this) {
            return vm.option === 'group'
        },
        subgroupOptions(vm = this) {
            return (vm.team?.groups || []).map((group, i) => ({
                ...group,
                name: group.name || `${vm.$t('common.subgroup')} ${i + 1}`
            }))
        },
        readonlyStudents(vm = this) {
            if (vm.isTeams) return !vm.subgroup
            if (vm.isGroups) return !vm.group
            return false
        },
        hasGroupSelected(vm = this) {
            return !!(vm.subgroup || vm.group)
        },
    },
    methods: {
        ...mapActions(['fetchCoachAssignedRoutinesOfDay', 'setLibraryCoach', 'fetchCompanyCoaches']),
        ...mapMutations(['resetCoachAssignedRoutines']),
        async loadCoaches() {
            const vm = this
            const hits = await vm.fetchCompanyCoaches(vm.currentUser.uid)
            vm.coaches = hits.map(hit => ({
                ...hit,
                name: hit.name || hit.email
            }))
            if (!vm.libraryCoach) return
            vm.currentCoach = vm.coaches.find(coach => coach.objectID === vm.libraryCoach?.id) || null
            if (vm.currentCoach) await vm.initZapping()
        },
        async initZapping() {
            const vm = this;
            vm.loading = true
            await vm.loadRoutines();
            vm.loading = false
        },
        async loadRoutines() {
            const vm = this;
            let payload = { coachId: vm.coachId, date: vm.$moment().format('YYYY-MM-DD') }
            await vm.fetchCoachAssignedRoutinesOfDay(payload)
            await vm.loadStudents()
        },
        async loadStudents() {
            const vm = this
            if (vm.coachAssignedRoutines.length === 0) {
                vm.optionStudents = []
                vm.routines = []
                return;
            }

            let currentStudentsFromGroup = []
            if (vm.hasGroupSelected) {
                currentStudentsFromGroup = vm.subgroup?.students || vm.group?.students || []
            }

            let coachAssignedRoutines = uniqueDayRoutine(vm.coachAssignedRoutines.filter(routine => {
                if (!vm.hasGroupSelected) return true
                return currentStudentsFromGroup.includes(routine.studentId)
            }))

            let routines = []
            for (let routine of coachAssignedRoutines) {
                let mergedBlocks = vm.mergedBlocksCache[routine.id]
                if (!mergedBlocks) {
                    mergedBlocks = await getMergedBlocks(routine.blocks)
                    vm.mergedBlocksCache[routine.id] = mergedBlocks
                }
                routines.push({ ...routine, blocks: mergedBlocks })
            }

            routines = routines.filter(r =>
                r.type !== 'competence' &&
                !r.advance?.post
            )

            const studentIds = [...new Set(routines.map(r => r.studentId))]
            if (studentIds.length === 0) {
                vm.optionStudents = []
                vm.routines = []
                return
            }

            try {
                const students = await getAllByIds('users', studentIds)
                const byId = new Map()
                vm.optionStudents = students.map(student => {
                    const mapped = { ...student, name: student.name || student.email }
                    byId.set(student.objectID, mapped)
                    return mapped
                })
                vm.routines = routines
                    .map(routine => ({ ...routine, student: byId.get(routine.studentId) }))
                    .filter(routine => routine.student)
                    .sort((a, b) => {
                        const nameA = a.student?.name?.toLowerCase() || "";
                        const nameB = b.student?.name?.toLowerCase() || "";
                        return nameA.localeCompare(nameB);
                    })
            } catch (err) {
                console.log(err);
            }
        },
        onReset() {
            const vm = this;
            vm.showRoutines = false
            vm.students = []
        },
        reset() {
            const vm = this;
            vm.optionStudents = []
            vm.students = []
            vm.team = null
            vm.subgroup = null
            vm.group = null
        },
        onSetCurrentCoach(coach) {
            const vm = this;
            if (coach.objectID === vm.currentCoach?.objectID) return
            if (vm.loading) return;
            vm.currentCoach = coach
            vm.setLibraryCoach(coach)
            vm.reset()
            vm.option = 'student'
            vm.showRoutines = false
            vm.routines = []
            vm.mergedBlocksCache = {}
            this.initZapping()
        },
    },
    watch: {
        locked: {
            immediate: true,
            handler() {
                const vm = this
                if (vm.locked || vm._started) return
                vm._started = true
                vm.resetCoachAssignedRoutines()
                vm.loadCoaches()
            }
        },
        option(val) {
            this.reset()
            if (val === 'student' && this.coachId) this.loadStudents()
        },
        team() {
            this.optionStudents = []
            this.students = []
            this.subgroup = null
        },
        subgroup() {
            if (this.coachId) this.loadStudents()
        },
        group() {
            if (this.coachId) this.loadStudents()
        }
    }
}
</script>

<style scoped>
.zapping-view {
    max-width: 1280px;
}

.zap-select {
    display: flex;
    justify-content: center;
    padding: 24px 8px;
}

.zap-select-card {
    width: 100%;
    max-width: 580px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.zap-select-desc {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    line-height: 1.6;
    text-align: center;
    margin: 0;
}
</style>
